import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Consulta de registros por formato (pagina del gerente).
// Flujo: tenant -> formato -> rango de fecha -> total de registros creados.
// Registros = surveys con state_db:true + state_complete:saved (backend).

export default function RecordsReport() {
  const [tenant, setTenant]   = useState(null);   // { id, name }
  const [format, setFormat]   = useState(null);   // { id, name }
  const [formats, setFormats] = useState([]);
  const [loadingFormats, setLoadingFormats] = useState(false);

  // Por defecto: "Ayer" (from = to = ayer), como si entrara y diera click en Ayer.
  const [from, setFrom] = useState(yesterdayISO());
  const [to, setTo]     = useState(yesterdayISO());

  const [result, setResult]   = useState(null);
  const [querying, setQuerying] = useState(false);
  const [error, setError]     = useState(null);

  // Al elegir tenant -> traer sus formatos
  const onPickTenant = useCallback((t) => {
    setTenant(t);
    setFormat(null);
    setFormats([]);
    setResult(null);
    if (!t) return;
    setLoadingFormats(true);
    fetch(`/settings/clients_v2/tenant_formats?tenant_id=${t.id}`, {
      headers: { Accept: 'application/json' }, credentials: 'same-origin',
    })
      .then(r => r.json())
      .then(j => setFormats(j.data || []))
      .catch(() => setFormats([]))
      .finally(() => setLoadingFormats(false));
  }, []);

  const canQuery = tenant && format && from && to && from <= to;

  const runQuery = useCallback(() => {
    if (!canQuery) return;
    setQuerying(true);
    setError(null);
    const p = new URLSearchParams({
      tenant_id: tenant.id, format_id: format.id, from, to,
    });
    fetch(`/settings/clients_v2/records_count?${p}`, {
      headers: { Accept: 'application/json' }, credentials: 'same-origin',
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { setResult(j); setQuerying(false); })
      .catch(e => { setError(e.message); setQuerying(false); });
  }, [canQuery, tenant, format, from, to]);

  return (
    <div className="cv2-page rr">
      <div className="rr__head">
        <div className="rr__head-icon"><i className="fas fa-chart-bar" /></div>
        <div>
          <h2 className="rr__title">Consulta de registros por formato</h2>
          <p className="rr__subtitle">
            Elige un cliente, un formato y un rango de fechas para ver cuántos
            registros se crearon.
          </p>
        </div>
        <a href="/settings/clients_v2/dashboard" className="rr__back">
          <i className="fas fa-arrow-left" /> Dashboard
        </a>
      </div>

      <div className="rr__panel">
        {/* Paso 1: cliente */}
        <div className="rr__field">
          <label className="rr__label">
            <span className="rr__step">1</span> Cliente
          </label>
          <TenantCombobox value={tenant} onPick={onPickTenant} />
        </div>

        {/* Paso 2: formato */}
        <div className={`rr__field ${!tenant ? 'rr__field--disabled' : ''}`}>
          <label className="rr__label">
            <span className="rr__step">2</span> Formato
          </label>
          <FormatCombobox
            disabled={!tenant}
            loading={loadingFormats}
            options={formats}
            value={format}
            onPick={(f) => { setFormat(f); setResult(null); }}
          />
          {tenant && !loadingFormats && (
            <span className="rr__hint">
              {formats.length} formato{formats.length === 1 ? '' : 's'} activo{formats.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {/* Paso 3: fechas */}
        <div className={`rr__field ${!format ? 'rr__field--disabled' : ''}`}>
          <label className="rr__label">
            <span className="rr__step">3</span> Rango de fechas de creación
          </label>
          <div className="rr__dates">
            <div className="rr__date">
              <span>Desde</span>
              <input type="date" value={from} max={to} disabled={!format}
                     onChange={e => setFrom(e.target.value)} />
            </div>
            <i className="fas fa-arrow-right rr__date-arrow" />
            <div className="rr__date">
              <span>Hasta</span>
              <input type="date" value={to} min={from} max={todayISO()} disabled={!format}
                     onChange={e => setTo(e.target.value)} />
            </div>
          </div>
          <div className="rr__presets">
            {DATE_PRESETS.map(p => (
              <button key={p.label} type="button" className="rr__preset" disabled={!format}
                      onClick={() => { const [f, t] = p.range(); setFrom(f); setTo(t); }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <button className="rr__submit" disabled={!canQuery || querying} onClick={runQuery}>
          {querying
            ? <><i className="fas fa-spinner fa-spin" /> Consultando…</>
            : <><i className="fas fa-search" /> Consultar registros</>}
        </button>
      </div>

      {error && <div className="cv2-error rr__result-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}

      {result && !error && (
        <ResultPanel result={result} tenant={tenant} format={format} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Resultado                                                           */
/* ------------------------------------------------------------------ */
function ResultPanel({ result, tenant, format }) {
  const daysWithData = result.by_day?.length || 0;
  const peak = useMemo(
    () => (result.by_day || []).reduce((m, d) => d.count > (m?.count || 0) ? d : m, null),
    [result.by_day]
  );

  return (
    <div className="rr__result">
      <div className="rr__result-head">
        <div>
          <span className="rr__result-eyebrow">{tenant?.name}</span>
          <h3 className="rr__result-format">{format?.name}</h3>
          <p className="rr__result-range">
            <i className="far fa-calendar" /> {fmt(result.from)} → {fmt(result.to)}
          </p>
        </div>
      </div>

      <div className="rr__kpis">
        <div className="rr__kpi rr__kpi--total">
          <div className="rr__kpi-value">{(result.total || 0).toLocaleString()}</div>
          <div className="rr__kpi-label">Registros creados</div>
        </div>
        <div className="rr__kpi">
          <div className="rr__kpi-value">{daysWithData.toLocaleString()}</div>
          <div className="rr__kpi-label">Días con actividad</div>
        </div>
        <div className="rr__kpi">
          <div className="rr__kpi-value">{peak ? peak.count.toLocaleString() : '0'}</div>
          <div className="rr__kpi-label">
            Máximo en un día{peak ? ` · ${fmt(peak.date)}` : ''}
          </div>
        </div>
      </div>

      {result.total > 0 ? (
        <div className="rr__chart-card">
          <h5>Registros por día</h5>
          <DailyBars data={result.by_day} />
        </div>
      ) : (
        <div className="rr__empty">
          <i className="far fa-folder-open" />
          <p>No se crearon registros de este formato en el rango seleccionado.</p>
        </div>
      )}
    </div>
  );
}

// Barras verticales por dia. Scroll horizontal solo si hay muchos dias.
function DailyBars({ data }) {
  if (!data?.length) return null;
  const H = 200;
  const PAD = { top: 18, right: 12, bottom: 46, left: 40 };
  const slot = 34;
  const innerH = H - PAD.top - PAD.bottom;
  const W = Math.max(640, PAD.left + PAD.right + data.length * slot);
  const maxY = Math.max(1, ...data.map(d => d.count));
  const yTicks = [0, 0.5, 1].map(p => Math.round(maxY * p));

  return (
    <div className="rr__chart-scroll">
      <svg width={W} height={H} className="rr__bars">
        {yTicks.map((tv, i) => {
          const y = PAD.top + innerH - (tv / maxY) * innerH;
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#eef0f3" />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{tv}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = (d.count / maxY) * innerH;
          const x = PAD.left + i * slot + 4;
          const y = PAD.top + innerH - h;
          const w = slot - 8;
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={w} height={h} rx="3" fill="#3b82f6">
                <title>{`${fmt(d.date)}: ${d.count.toLocaleString()} registros`}</title>
              </rect>
              {h > 14 && (
                <text x={x + w / 2} y={y - 4} textAnchor="middle" fontSize="10" fontWeight="600" fill="#1e293b">
                  {d.count}
                </text>
              )}
              <text x={x + w / 2} y={H - 26} textAnchor="end" fontSize="9" fill="#64748b"
                    transform={`rotate(-55, ${x + w / 2}, ${H - 26})`}>
                {d.date.slice(5)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Combobox tenant (autocomplete server-side)                         */
/* ------------------------------------------------------------------ */
function TenantCombobox({ value, onPick }) {
  const [open, setOpen]     = useState(false);
  const [q, setQ]           = useState('');
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(false);
  const boxRef = useRef(null);
  const debounce = useRef(null);

  useOutsideClose(boxRef, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setLoad(true);
      fetch(`/settings/clients_v2/tenant_search?q=${encodeURIComponent(q)}`, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin',
      })
        .then(r => r.json())
        .then(j => setItems(j.data || []))
        .catch(() => setItems([]))
        .finally(() => setLoad(false));
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [q, open]);

  return (
    <div className="rr-combo" ref={boxRef}>
      <button type="button" className={`rr-combo__control ${value ? 'is-filled' : ''}`}
              onClick={() => setOpen(o => !o)}>
        <i className="fas fa-building rr-combo__lead" />
        <span className="rr-combo__value">{value ? value.name : 'Selecciona un cliente…'}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} rr-combo__caret`} />
      </button>
      {open && (
        <div className="rr-combo__pop">
          <div className="rr-combo__search">
            <i className="fas fa-search" />
            <input autoFocus type="text" placeholder="Buscar cliente…"
                   value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <div className="rr-combo__list">
            {loading && <div className="rr-combo__msg"><i className="fas fa-spinner fa-spin" /> Buscando…</div>}
            {!loading && items.length === 0 && <div className="rr-combo__msg">Sin resultados</div>}
            {!loading && items.map(it => (
              <button key={it.id} type="button"
                      className={`rr-combo__item ${value?.id === it.id ? 'is-active' : ''}`}
                      onClick={() => { onPick(it); setOpen(false); setQ(''); }}>
                {it.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Combobox formato (filtro client-side)                              */
/* ------------------------------------------------------------------ */
function FormatCombobox({ disabled, loading, options, value, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState('');
  const boxRef = useRef(null);
  useOutsideClose(boxRef, () => setOpen(false));

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return ql ? options.filter(o => o.name.toLowerCase().includes(ql)) : options;
  }, [q, options]);

  const placeholder = loading ? 'Cargando formatos…'
    : disabled ? 'Primero elige un cliente'
    : 'Selecciona un formato…';

  return (
    <div className="rr-combo" ref={boxRef}>
      <button type="button" className={`rr-combo__control ${value ? 'is-filled' : ''}`}
              disabled={disabled || loading}
              onClick={() => setOpen(o => !o)}>
        <i className="fas fa-clipboard-list rr-combo__lead" />
        <span className="rr-combo__value">{value ? value.name : placeholder}</span>
        {loading ? <i className="fas fa-spinner fa-spin rr-combo__caret" />
          : <i className={`fas fa-chevron-${open ? 'up' : 'down'} rr-combo__caret`} />}
      </button>
      {open && !disabled && (
        <div className="rr-combo__pop">
          <div className="rr-combo__search">
            <i className="fas fa-search" />
            <input autoFocus type="text" placeholder="Filtrar formato…"
                   value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <div className="rr-combo__list">
            {filtered.length === 0 && <div className="rr-combo__msg">Sin formatos</div>}
            {filtered.map(o => (
              <button key={o.id} type="button"
                      className={`rr-combo__item ${value?.id === o.id ? 'is-active' : ''}`}
                      onClick={() => { onPick(o); setOpen(false); setQ(''); }}>
                {o.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function useOutsideClose(ref, cb) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, cb]);
}

function todayISO() {
  const d = new Date();
  return localISO(d);
}
function yesterdayISO() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return localISO(d);
}
function localISO(d) {
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}
function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return iso; }
}

const DATE_PRESETS = [
  { label: 'Hoy',        range: () => { const t = todayISO(); return [t, t]; } },
  { label: 'Ayer',       range: () => { const d = new Date(); d.setDate(d.getDate() - 1); const y = localISO(d); return [y, y]; } },
  { label: 'Últimos 7d', range: () => { const d = new Date(); d.setDate(d.getDate() - 6); return [localISO(d), todayISO()]; } },
  { label: 'Últimos 30d', range: () => { const d = new Date(); d.setDate(d.getDate() - 29); return [localISO(d), todayISO()]; } },
  { label: 'Este mes',   range: () => { const d = new Date(); const first = new Date(d.getFullYear(), d.getMonth(), 1); return [localISO(first), todayISO()]; } },
  { label: 'Este año',   range: () => { const d = new Date(); const first = new Date(d.getFullYear(), 0, 1); return [localISO(first), todayISO()]; } },
];
